import Vue from "vue";
import { OrderStatus } from "@/models/order/helpers";
import {
  LineItem,
  LineItemRecord,
  Order,
  OrderDetails
} from "@/models/order/order";
import {
  DriftReport,
  QuoteDraftLine,
  QuoteDraftStatus
} from "@/models/quote_draft/quote_draft";
import { OrderState } from "./types";

export const setLineItem = (
  state: OrderState,
  value: LineItem | LineItemRecord
) => {
  state.items?.push(value as any);
};

export const editLineItem = (
  state: OrderState,
  value: { item: LineItem; index: number }
) => {
  state.items!.splice(value.index, 1, value.item);
};

export const setAllLineItems = (state: OrderState, value: LineItem[] | []) => {
  state.items = value;
};

export const setStatus = (state: OrderState, value: OrderStatus) => {
  state.status = value;
};

export const setNotes = (state: OrderState, value: string) => {
  state.notes = value;
};

export const setOrderId = (
  state: OrderState,
  value: { id: string }
) => {
  state._id = value.id;
};

export const setShipping = (
  state: OrderState,
  value: OrderDetails["shipping_details"]
) => {
  const obj = Object.assign({}, { ...state.shipping_details! }, { ...value });
  state.shipping_details = obj;
};

export const setBilling = (
  state: OrderState,
  value: OrderDetails["billing_details"]
) => {
  const obj = Object.assign({}, { ...state.billing_details! }, { ...value });
  state.billing_details = obj;
};

export const setPayment = (
  state: OrderState,
  value: OrderDetails["payment_details"]
) => {
  const obj = Object.assign({}, { ...state.payment_details! }, { ...value });
  state.payment_details = obj;
};

export const setContact = (
  state: OrderState,
  value: OrderDetails["contact_details"]
) => {
  const obj = Object.assign({}, { ...state.contact_details }, { ...value });
  state.contact_details = obj;
};

/**
 * FUPOF-191: `Object.assign` copies a key whose value is `undefined` just like
 * any other, so spreading `value` used to overwrite a perfectly good blank
 * section with nothing. `loadDraft` hands this mutation
 * `header_payload.shipping_details` (etc.) straight through, and a header that
 * omits a section — every adopted NetSuite quote, whose header_payload is the
 * customer block alone, plus any draft written before a section existed — then
 * left `state.shipping_details` / `billing_details` / `payment_details` as
 * `undefined`. That is what made Quote Details come up empty (and any consumer
 * reading `order.payment_details.method` without a guard throw outright).
 *
 * So each section is only replaced when the incoming order actually carries
 * one; otherwise the state keeps the blank `clearOrder` just installed.
 */
export const setOrder = (state: OrderState, value: Order) => {
  const obj = Object.assign({}, { ...state }, { ...value });
  if (obj.billing_details) state.billing_details = obj.billing_details;
  if (obj.contact_details) state.contact_details = obj.contact_details;
  if (obj.payment_details) state.payment_details = obj.payment_details;
  if (obj.shipping_details) state.shipping_details = obj.shipping_details;
  state._id = obj._id;
  state.notes = obj.notes;
  state.created_type = obj.created_type;
};

export const clearOrder = (state: OrderState, value: Order) => {
  state.billing_details = value.billing_details;
  state.contact_details = value.contact_details;
  state.payment_details = value.payment_details;
  state.shipping_details = value.shipping_details;
  state.notes = "";
  state._id = null;
  state.created_type = undefined;
  state.netsuite_id = undefined;
  state.netsuite_tran_id = undefined;
  state.draft_id = null;
  state.draft_status = null;
  state.drift_report = null;
};

/**
 * FUPOF-191: hard reset of the whole module. Unlike clearOrder — which leaves
 * the in-flow fields (status, type, status_log, cost_details and the customer
 * block) alone because the same user is still working — this puts every key
 * back to its default. Used when the identity behind the session changes
 * (different user signs in, impersonation is left), where nothing from the
 * previous session may survive into the next one.
 *
 * Keys that aren't in the default state are deleted rather than left in place,
 * so a field written by an older build and still sitting in the persisted
 * localStorage payload can't outlive the reset. `value` must be a fresh deep
 * copy of the defaults — assigning the module-level singleton's nested objects
 * by reference would let the next in-place edit corrupt them.
 */
export const resetState = (state: OrderState, value: OrderState) => {
  Object.keys(state).forEach(key => {
    if (!(key in value)) Vue.delete(state, key);
  });
  Object.keys(value).forEach(key => {
    Vue.set(state, key, (value as any)[key]);
  });
};

export const setDraftMeta = (
  state: OrderState,
  value: {
    draft_id: number | null;
    draft_status: QuoteDraftStatus | null;
    netsuite_id?: number | null;
    netsuite_tran_id?: string | null;
  }
) => {
  state.draft_id = value.draft_id;
  state.draft_status = value.draft_status;
  if (value.netsuite_id !== undefined) {
    state.netsuite_id = value.netsuite_id ?? undefined;
  }
  if (value.netsuite_tran_id !== undefined) {
    state.netsuite_tran_id = value.netsuite_tran_id ?? undefined;
  }
};

/**
 * The NetSuite document number (e.g. "QUO2400") isn't part of the PSQL draft —
 * it only rides along on the NetSuite quote row. The edit flow sets it here so
 * the edit-mode banner can name the quote the way NetSuite does.
 */
export const setNetSuiteTranId = (
  state: OrderState,
  tranId: string | null
) => {
  state.netsuite_tran_id = tranId ?? undefined;
};

/**
 * Merges API-minted uuid / ns_line_id values back onto the store's line items
 * by position. Deliberately a separate mutation from setAllLineItems so that
 * id-stamping isn't treated as a content change (which matters again if the
 * parked draft auto-save plugin is reinstated — see
 * .claude/context/FUPOF-187/FUPOF-191/AUTO-SAVE-PARKED.md).
 */
export const setDraftLineIds = (state: OrderState, lines: QuoteDraftLine[]) => {
  if (!state.items) return;
  state.items = state.items.map((item, i) => ({
    ...item,
    uuid: lines[i]?.uuid ?? item.uuid,
    ns_line_id: lines[i]?.ns_line_id ?? item.ns_line_id
  }));
};

export const setDriftReport = (
  state: OrderState,
  value: DriftReport | null
) => {
  state.drift_report = value;
};

export const setCustomer = (
  state: OrderState,
  value: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    order_id: string | null;
  }
) => {
  state.customer_id = value.customer_id;
  state.customer_name = value.customer_name;
  state.customer_netsuite_id = value.customer_netsuite_id;
  state.customer_netsuite_contact_id = value.customer_netsuite_contact_id;
  state.customer_netsuite_payment_terms_id =
    value.customer_netsuite_payment_terms_id;
  state.customer_netsuite_price_level_id =
    value.customer_netsuite_price_level_id;
  state.customer_netsuite_price_level_discount =
    value.customer_netsuite_price_level_discount;
  state._id = value.order_id;
};

/**
 * FUPOF-191: pin the NetSuite contact this order is quoted to.
 *
 * Set from the submit response, where the contact id is known for the first
 * time on a quote whose contact was typed in rather than picked — NetSuite
 * mints the Contact record during the submit. Both places the id is read from
 * are written: `customer_netsuite_contact_id` is what the submit payload
 * prefers, and `contact_details.contact_id` is what the checkout form's Select
 * Contact dropdown binds to.
 */
export const setCustomerContact = (state: OrderState, value: number) => {
  state.customer_netsuite_contact_id = value;
  state.contact_details = Object.assign({}, { ...state.contact_details }, {
    contact_id: value
  });
};

/**
 * FUPOF-191: these are assigned `undefined`, never `delete`d.
 *
 * `delete` on Vue 2 state removes the reactive getter/setter with it and
 * notifies nobody, so every later write lands on a plain property that no
 * computed is watching. `defaultState` declares each of these keys precisely so
 * they are reactive from init, and one `delete` here undid that for the rest of
 * the session: clearing the customer and then picking another left
 * `isNewCustomer` stuck at whatever it read last, so the Quote Contact form
 * kept showing "Select Contact" for a New Customer quote — and showed no
 * Company Name field to fill in — while `customer_id` (assigned, not deleted)
 * updated normally and hid the "select a customer" banner. A page refresh
 * rehydrated the store and hid the whole thing again.
 */
export const clearCustomer = (state: OrderState, value: null) => {
  state.customer_id = "";
  state.customer_name = "";
  state.customer_netsuite_id = undefined;
  state.customer_netsuite_contact_id = undefined;
  state.customer_netsuite_payment_terms_id = undefined;
  state.customer_netsuite_price_level_id = undefined;
  state.customer_netsuite_price_level_discount = undefined;
  state._id = null;
};

export const clearCreatedType = (state: OrderState, value: null) => {
  state.created_type = undefined;
};
